import { useEffect, useRef } from 'react';
import APIAmplify from '@aws-amplify/api';

import Constant from '@/common/constant/Constant';
import Logger from '@/common/utils/Logger';
import { isJobOfSourceFile } from '@/common/utils/translationJobUtils';
import { onTranslationJobUpdated } from '@/graphql/subscriptions';

const TAG = 'useTranslationJobSubscription';

/**
 * Live translation status for ONE attachment.
 *
 * Unrelated to `useTranslationSubscription` in @/hooks/useTranslation - that one is a Recoil
 * re-render trigger for the i18n dictionary, not a GraphQL subscription.
 *
 * @param {Object}   params
 * @param {string}   params.affiliationOrganization organization owning the content
 * @param {string}   params.sourceS3Url             source key with the public/ prefix
 * @param {boolean}  params.isEnabled               true while the dialog is open
 * @param {Function} params.onJobUpdated            receives every active job belonging to this file
 * @param {Function} [params.onJobInvalidated]      receives a job of this file whose source was replaced/deleted
 */
const useTranslationJobSubscription = ({ affiliationOrganization, sourceS3Url, isEnabled, onJobUpdated, onJobInvalidated }) => {
    // Held in a ref so the effect below depends only on primitives: with the callback in the
    // dependency list, every keystroke in the language search box would tear down the WebSocket
    // and open a new one.
    const onJobUpdatedRef = useRef(onJobUpdated);
    const onJobInvalidatedRef = useRef(onJobInvalidated);

    useEffect(() => {
        onJobUpdatedRef.current = onJobUpdated;
        onJobInvalidatedRef.current = onJobInvalidated;
    }, [onJobUpdated, onJobInvalidated]);

    useEffect(() => {
        if (!isEnabled || !affiliationOrganization || !sourceS3Url) return () => {};

        // onTranslationJobUpdated is annotated @aws_api_key in the schema, and a field directive
        // overrides the schema default (AMAZON_COGNITO_USER_POOLS). Without this the socket is
        // rejected with "Not Authorized" and no event ever reaches `next`.
        const subscription = APIAmplify.graphql({
            query: onTranslationJobUpdated,
            variables: { affiliationOrganization },
            authMode: Constant.AUTH_MODE_API_KEY
        }).subscribe({
            next: resData => {
                const job = resData?.value?.data?.onTranslationJobUpdated;
                // The subscription carries every job of the organization - AI Chat translations and
                // other attachments included - so the file + entry-point filter is what makes it usable here.
                if (!isJobOfSourceFile(job, sourceS3Url, affiliationOrganization, Constant.TRANSLATION_REQUEST_SOURCE.ATTACHMENT)) {
                    return;
                }

                if (job.invalidatedAt) {
                    onJobInvalidatedRef.current?.(job);
                    return;
                }

                onJobUpdatedRef.current?.(job);
            },
            // A dropped socket is a background failure, not something the user did: log it and let
            // the next open of the dialog reload the state from the server.
            error: err => Logger.error(`${TAG} onTranslationJobUpdated`, err)
        });

        return () => subscription?.unsubscribe();
    }, [isEnabled, affiliationOrganization, sourceS3Url]);
};

export default useTranslationJobSubscription;
